import { useEffect, useState, useRef, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Geolocation } from '@pass-frontend/data-access/common/edge';
import { GET_LOCATION_SUGGESTIONS } from '@pass-frontend/features/location-typeahead/data-access';

export interface LocationTypeaheadOptions {
  biasLatitude?: number;
  biasLongitude?: number;
}

export function useLocationTypeahead(options?: LocationTypeaheadOptions) {
  const [fetchLocationSuggestionsQuery, { loading, error, data }] =
    useLazyQuery<{
      locationSuggestions: Geolocation[];
    }>(GET_LOCATION_SUGGESTIONS);

  const [noSuggestionsAvailable, setNoSuggestionsAvailable] = useState(false);

  // Use ref to track latest bias coordinates
  const biasLatitudeRef = useRef(options?.biasLatitude);
  const biasLongitudeRef = useRef(options?.biasLongitude);

  // Update refs when bias coordinates change
  useEffect(() => {
    biasLatitudeRef.current = options?.biasLatitude;
    biasLongitudeRef.current = options?.biasLongitude;
  }, [options?.biasLatitude, options?.biasLongitude]);

  useEffect(() => {
    if (loading === false) {
      setNoSuggestionsAvailable(data?.locationSuggestions?.length === 0);
    }
  }, [data, loading]);

  const fetchLocationSuggestions = useCallback(
    (query: string) => {
      fetchLocationSuggestionsQuery({
        variables: {
          query,
          biasLatitude: biasLatitudeRef.current,
          biasLongitude: biasLongitudeRef.current,
        },
      });
    },
    [fetchLocationSuggestionsQuery],
  );

  return {
    loading,
    error,
    data,
    fetchLocationSuggestions,
    noSuggestionsAvailable,
  };
}
